export interface JwtAccessPayload {
  sub?: string;
  tenant?: string;
  type?: string;
  role?: string | number;
  plan?: string;
  planType?: string;
  modules?: string[];
  exp?: number;
  [key: string]: unknown;
}

export type AppRole = number | string | null | undefined;

const MODULE_ALIASES: Record<string, string> = {
  TASKS: "TASK",
};

// Email domain that identifies internal Liri administrators
const LIRI_DOMAIN = "@lirisoft.com";

const MODULE_AUTH_ROLES = new Set([
  "1",
  "USER",
  "ROLE_USER",
  "2",
  "ADMIN",
  "ROLE_ADMIN",
  "4",
  "MANAGER",
  "ROLE_MANAGER",
]);

const normalizeModule = (moduleName: string) => {
  const normalized = moduleName.trim().toUpperCase();
  return MODULE_ALIASES[normalized] || normalized;
};
const normalizePlan = (planName: string) => planName.trim().toUpperCase();

const decodeBase64Url = (value: string): string | null => {
  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return atob(padded);
  } catch {
    return null;
  }
};

export const parseJwtPayload = (token?: string | null): JwtAccessPayload | null => {
  if (!token) return null;
  console.log("parseJwtPayload token:", token);

  const parts = token.split(".");
  if (parts.length < 2) return null;

  const decoded = decodeBase64Url(parts[1]);
  const decodePayload = decodeBase64Url(parts[2]);
  console.log("parseJwtPayload decodePayload:", decodePayload);
  if (!decoded) return null;
  console.log("parseJwtPayload decoded:", decoded);

  try {
    const payload = JSON.parse(decoded) as JwtAccessPayload;
    console.log("parseJwtPayload payload:", payload);
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
};

export const getModulesFromPayload = (payload: JwtAccessPayload | null): string[] => {
  if (!payload || !Array.isArray(payload.modules)) {
    return [];
  }

  return payload.modules
    .filter((moduleName): moduleName is string => typeof moduleName === "string")
    .map(normalizeModule);
};

export const getPlanType = (payload: JwtAccessPayload | null): string => {
  const rawPlan =
    (typeof payload?.planType === "string" ? payload.planType : undefined) ||
    (typeof payload?.plan === "string" ? payload.plan : undefined);

  if (!rawPlan) {
    return "UNKNOWN";
  }

  return normalizePlan(rawPlan);
};

export const getNormalizedModules = (payload: JwtAccessPayload | null): string[] => {
  return getModulesFromPayload(payload);
};

const normalizeRole = (role: AppRole): string => {
  if (typeof role === "number") {
    return String(role);
  }

  if (typeof role === "string") {
    return role.trim().toUpperCase();
  }

  return "";
};

export const getRoleFromPayload = (payload: JwtAccessPayload | null): string => {
  return normalizeRole(payload?.role);
};

const getEffectiveRole = (
  payload: JwtAccessPayload | null,
  currentRole?: AppRole,
): string => {
  const payloadRole = getRoleFromPayload(payload);
  if (payloadRole) {
    return payloadRole;
  }

  return normalizeRole(currentRole);
};

export const isAdminRole = (
  payload: JwtAccessPayload | null,
  currentRole?: AppRole,
): boolean => {
  const role = getEffectiveRole(payload, currentRole);
  return role === "2" || role === "ADMIN"|| role === "ROLE_ADMIN";
};

export const isUserRole = (
  payload: JwtAccessPayload | null,
  currentRole?: AppRole,
): boolean => {
  const role = getEffectiveRole(payload, currentRole);
  return role === "1" || role === "USER"|| role === "ROLE_USER";
};

export const isManagerRole = (
  payload: JwtAccessPayload | null,
  currentRole?: AppRole,
): boolean => {
  const role = getEffectiveRole(payload, currentRole);
  return role === "4" || role === "MANAGER" || role === "ROLE_MANAGER";
};

export const canAccess = (
  payload: JwtAccessPayload | null,
  requiredModule?: string,
  currentRole?: AppRole,
): boolean => {
  if (!requiredModule) {
    return true;
  }

  const effectiveRole = getEffectiveRole(payload, currentRole);
  const shouldApplyModuleAuth = MODULE_AUTH_ROLES.has(effectiveRole);

  if (!shouldApplyModuleAuth) {
    return true;
  }

  const normalizedRequiredModule = normalizeModule(requiredModule);
  const modules = getModulesFromPayload(payload);

  // Strict admin module enforcement for plan/module-based auth.
  if (modules.length === 0) {
    return false;
  }

  return modules.includes(normalizedRequiredModule);
};

export const hasModuleAccess = (
  payload: JwtAccessPayload | null,
  requiredModule?: string,
  currentRole?: AppRole,
): boolean => canAccess(payload, requiredModule, currentRole);

export const isPremiumPlanUser = (payload: JwtAccessPayload | null): boolean => {
  return getPlanType(payload) === "PREMIUM";
};

export const getPostLoginRedirectPath = (token?: string | null): string => {
  const payload = parseJwtPayload(token);
  return isPremiumPlanUser(payload) ? "/admin/profile" : "/admin/profile";
};

export const logAuthPayload = (token: string, source: string): void => {
  const payload = parseJwtPayload(token);
  const extractedRole = getRoleFromPayload(payload);
  const extractedModules = getModulesFromPayload(payload);
  console.log(`[login token]`, { token });
  console.log(`[login  payload]`, {  payload });
  console.log(`[Auth] Role (${source}):`, extractedRole || "UNKNOWN");
  console.log(`[Auth] Plan type (${source}):`, payload?.plan ?? payload?.planType ?? "UNKNOWN");
  console.log(`[Auth] Modules (${source}):`, Array.isArray(payload?.modules) ? payload?.modules : []);
  console.log(`[Auth] Extracted modules (${source}):`, extractedModules);
};


export const isLiriAdmin = (
  payload: JwtAccessPayload | null,
  currentRole?: AppRole,
  userEmail?: string | null, // <-- Add this parameter
): boolean => {
  const isAdmin = isAdminRole(payload, currentRole);

  // Verify the provided email ends with the required domain
  const isLirisoftDomain = userEmail?.toLowerCase().endsWith(LIRI_DOMAIN);

  return isAdmin && !!isLirisoftDomain;
};